import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { toast } from "@shared/ui/use-toast";
import { axNetworksApi } from "./api";

export const useBackupStatus = () => {
  return useSmartQuery(QueryKeyFactory.module.ax.networks.backupStatus(), () =>
    axNetworksApi.getBackupStatus()
  );
};

export const useNetworkType = () => {
  return useSmartQuery(QueryKeyFactory.module.ax.networks.networkType(), () =>
    axNetworksApi.getNetworkType()
  );
};

export const useOperationalStatus = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.operationalStatus(),
    () => axNetworksApi.getOperationalStatus()
  );
};

export const useOriginFacilities = (
  params: {
    remote_utility_id: number;
    is_all_data: any;
  },
  enabled = true
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.originFacilities(params),
    () => axNetworksApi.getOriginFacilities(params),
    {
      enabled: enabled && Boolean(params.remote_utility_id),
    }
  );
};

// export const useUserInfo = () => {
//   return useSmartQuery(QueryKeyFactory.module.ax.networks.userInfo(), () =>
//     axNetworksApi.getUserInfo()
//   );
// };

export const useUserInfo = (
  params: {
    remote_utility_id: number;
    page: any;
    limit: any;
    role: string;
  },
  enabled = true
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.userInfo(params),
    () => axNetworksApi.getUserInfo(params),
    {
      enabled: enabled && Boolean(params.remote_utility_id),
    }
  );
};
export const useNetworksSummary = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.networkSummary(),
    () => axNetworksApi.getNetworksSummary()
  );
};

export const useNetworkList = (filters?: Record<string, any>) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.networkList(filters),
    () => axNetworksApi.getNetworksList(filters)
  );
};

export const useComplianceOverview = (
  params: {
    remote_utility_id: any;
    entity_type: any;
    entity_id: any;
  },
  enabled = true
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.complianceOverview(params),
    () => axNetworksApi.getComplianceOverview(params),
    {
      enabled:
        enabled &&
        Boolean(params.remote_utility_id) &&
        Boolean(params.entity_id),
    }
  );
};

export const useAssetList = (
  params: {
    remote_utility_id: any;
    page: number;
    limit: number;
    network: any;
  },
  enabled = true
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.assetList(params),
    () => axNetworksApi.getAssetList(params),
    {
      enabled:
        enabled && Boolean(params.remote_utility_id) && Boolean(params.network),
    }
  );
};

export const useComplianceHistory = (
  params: {
    entity_type: any;
    entity_id: any;
  },
  enabled = true
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.complianceHistory(params),
    () => axNetworksApi.getComplianceHistory(params),
    {
      enabled: enabled && Boolean(params.entity_id),
    }
  );
};

export const useCriticalityData = (
  params: {
    remote_utility_id: any;
    show_inactive: string;
  },
  enabled = true
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.criticalityData(params),
    () => axNetworksApi.getCriticalityData(params),
    {
      enabled: enabled && Boolean(params.remote_utility_id),
    }
  );
};

export const useAddNetwork = () => {
  return useSmartMutation(
    (payload: any) => axNetworksApi.addNetwork(payload),

    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.networks
            .networkList()
            .slice(0, 2),
        });
      },
    }
  );
};

export const useUpdateNetwork = () => {
  return useSmartMutation(
    ({ networkId, payload }: { networkId: string; payload: any }) =>
      axNetworksApi.updateNetwork(networkId, payload),
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.networks
            .networkList()
            .slice(0, 2),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.networks
            .networkSummary()
            .slice(0, 3),
        });
      },
    }
  );
};

export const useUpdateCriticalityAssessment = () => {
  return useSmartMutation(
    ({ networkId, payload }: { networkId: string; payload: any }) =>
      axNetworksApi.updateCriticalityAssessment(networkId, payload)
  );
};

export const useAddDocuments = () => {
  return useSmartMutation(
    ({ networkId, payload }: { networkId: string; payload: any }) =>
      axNetworksApi.addDocuments(networkId, payload),
    {
      onSuccess: (data, variables) => {
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.networks.details({
            networkId: variables.networkId,
          }),
        });
      },
    }
  );
};

export const useUnits = (
  params: {
    remote_utility_id: number;
    utility_service: string;
  },
  enabled = true
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.units(params),
    () => axNetworksApi.getUnits(params),
    {
      enabled:
        enabled &&
        Boolean(params.remote_utility_id) &&
        Boolean(params.utility_service),
    }
  );
};

export const useTerritoryZones = (
  params: {
    remote_utility_id: number;
    show_inactive: boolean;
  },
  enabled = true
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.territoryZones(params),
    () => axNetworksApi.getTerritoryZones(params),
    {
      enabled: enabled && Boolean(params.remote_utility_id),
    }
  );
};

export const useTerritoryAreas = (
  params: {
    remote_utility_id: number;
    show_inactive: boolean;
  },
  enabled = true
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.territoryAreas(params),
    () => axNetworksApi.getTerritoryAreas(params),
    {
      enabled: enabled && Boolean(params.remote_utility_id),
    }
  );
};

export const useTransmissionNetworks = (
  params: {
    remote_utility_id: number;
    is_pagination_required: number;
  },
  enabled = true
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.transmissionNetworks(params),
    () => axNetworksApi.getTransmissionNetworks(params),
    {
      enabled: enabled && Boolean(params.remote_utility_id),
    }
  );
};

export const useDMADistributionNetworks = (
  params: {
    remote_utility_id: number;
    is_pagination_required: number;
  },
  enabled = true
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.dmaDistributionNetworks(params),
    () => axNetworksApi.getDMADistributionNetworks(params),
    {
      enabled: enabled && Boolean(params.remote_utility_id),
    }
  );
};

export const useDistributionNetworks = (
  params: {
    remote_utility_id: number;
    is_pagination_required: number;
  },
  enabled = true
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.distributionNetworks(params),
    () => axNetworksApi.getDistributionNetworks(params),
    {
      enabled: enabled && Boolean(params.remote_utility_id),
    }
  );
};

// Generic hook for getting networks by type
export const useDestinationNetwork = (
  params: {
    type: string;
    remote_utility_id: number;
    is_pagination_required: number;
  },
  enabled = true
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.destinationNetwork(params),
    () => axNetworksApi.getDestinationNetwork(params),
    {
      enabled:
        enabled && Boolean(params.type) && Boolean(params.remote_utility_id),
    }
  );
};

export const useNetworkDetails = (
  params: {
    networkId: string;
    tab: string;
    remote_utility_id?: any;
  },
  enabled = true
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.networkDetails(params),
    () => axNetworksApi.getNetworkDetails(params),
    {
      enabled: enabled && Boolean(params.networkId) && Boolean(params.tab),
    }
  );
};

export const useDetails = (
  params: {
    networkId: string;
  },
  enabled = true
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.details(params),
    () => axNetworksApi.getDetails(params),
    {
      enabled: enabled && Boolean(params.networkId),
    }
  );
};

export const useDocuments = (
  params: {
    networkId: string;
  },
  enabled = true
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.documents(params),
    () => axNetworksApi.getDocuments(params),
    {
      enabled: enabled && Boolean(params.networkId),
    }
  );
};

export const useServiceOrderData = (
  params: {
    remote_utility_id: any;
    request_id: any;
  },
  enabled = true
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.serviceOrder(params),
    () => axNetworksApi.getServiceOrderData(params),
    {
      enabled:
        enabled &&
        Boolean(params.remote_utility_id) &&
        Boolean(params.request_id),
    }
  );
};

export const useAssetsList = (filters: any) => {
  const remoteUtilityId = useRemoteUtilityId();

  const search = filters.searchData || "";
  const systemNetwork = filters.systemNetwork;
  const location = "";
  const conditionScore = filters.conditionScore;

  const facilityId = filters.facilityId || 0;

  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.assets.list(
      remoteUtilityId,
      search,
      systemNetwork,
      location,
      conditionScore,
      facilityId
    ),
    () => axNetworksApi.getAssets(remoteUtilityId, filters),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useDeleteNetwork = () => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartMutation(
    ({ networkId: networkId }: { networkId: string }): Promise<any> => {
      return axNetworksApi.deleteNetwork(networkId, remoteUtilityId);
    },
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Network deleted successfully",
        });

        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.networks
            .networkList()
            .slice(0, 2),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.networks
            .networkSummary()
            .slice(0, 3),
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to delete Network",
          variant: "destructive",
        });
      },
    }
  );
};
